import { createAnthropic } from '@ai-sdk/anthropic'
import { createGoogleGenerativeAI } from '@ai-sdk/google'
import { createOpenAI } from '@ai-sdk/openai'
import { customProvider } from 'ai'
import * as z from 'zod'

/** AI providers and their required env vars */

export const aiProviderSchema = z.enum(['openai', 'anthropic', 'gemini'])

export interface AIProviderSpec {
  baseURL: string

  authHeader: {
    name: string
    bearer: boolean
  }

  getTokensUsed: (response: unknown) => number

  // Path to the secret containing the API key in Parameter Store
  apiKeySecret: string
}

export type AIProvider = z.infer<typeof aiProviderSchema>

const providerSpecs = {
  openai: {
    baseURL: 'https://api.openai.com/v1',
    authHeader: {
      name: 'Authorization',
      bearer: true,
    },
    apiKeySecret: '/tunasong/openai/api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usage: z.object({
          total_tokens: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usage.total_tokens
    },
  },
  anthropic: {
    baseURL: 'https://api.anthropic.com/v1',
    authHeader: {
      name: 'x-api-key',
      bearer: false,
    },
    apiKeySecret: '/tunasong/anthropic/api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usage: z.object({
          input_tokens: z.number(),
          output_tokens: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usage.input_tokens + parsedResponse.usage.output_tokens
    },
  },
  gemini: {
    baseURL: 'https://generativelanguage.googleapis.com/v1beta',
    authHeader: {
      name: 'x-goog-api-key',
      bearer: false,
    },
    apiKeySecret: '/tunasong/google/gemini-api-key',
    getTokensUsed: (response: unknown) => {
      const schema = z.object({
        usageMetadata: z.object({
          totalTokenCount: z.number(),
        }),
      })
      const parsedResponse = schema.parse(response)
      return parsedResponse.usageMetadata.totalTokenCount
    },
  },
} satisfies Record<AIProvider, AIProviderSpec & Record<string, unknown>>

/**
 * Resolve the provider for a model id. Accepts a retired id (see
 * `legacyModelAliases`) because stored agent configs reach here through an
 * `as SupportedModel` cast, not through `aiModelsSchema`.
 */
export const getProviderByModel = (model: SupportedModel) => {
  const resolved = normalizeModelId(model)
  for (const provider in supportedModelsByProvider) {
    if (supportedModelsByProvider[provider].includes(resolved)) {
      return provider as AIProvider
    }
  }
  return null
}

/**
 * Sampling parameters the Anthropic Messages API no longer accepts.
 *
 * These are the Vercel AI SDK's camelCase names; the Anthropic adapter maps
 * them to `temperature` / `top_p` / `top_k` on the wire.
 */
const anthropicUnsupportedSamplingParams = ['temperature', 'topP', 'topK'] as const

/**
 * Drop sampling parameters that the resolved provider rejects.
 *
 * `temperature`, `top_p` and `top_k` are **removed** on current Anthropic
 * models (Claude Opus 4.7 and later, Claude Sonnet 5) — sending any of them
 * returns a 400. Our request schemas carry `.default()`s
 * (`chat.schema.ts`, `text-params.ts`), so `temperature` is present on every
 * outbound request whether or not a caller asked for one.
 *
 * Applied here, at the single outbound chokepoint every text call passes
 * through, rather than at each of the five call sites. The fields stay on the
 * public request schemas: OpenAI and Gemini still accept them, and only the
 * Anthropic request omits them.
 *
 * Provider-conditional, not model-conditional, on purpose: it must stay
 * correct across Anthropic id bumps without a second table to keep in sync,
 * and Anthropic's own guidance is to steer behaviour by prompting instead.
 */
export const stripUnsupportedSamplingParams = <T extends object>(model: SupportedModel, params: T): T => {
  if (getProviderByModel(model) !== 'anthropic') {
    return params
  }
  const dropped: readonly string[] = anthropicUnsupportedSamplingParams
  return Object.fromEntries(Object.entries(params).filter(([key]) => !dropped.includes(key))) as T
}

export const getMinuteCostByTranscriptionModel = (model: SupportedTranscribeModel) => transcribeModelCost[model]

/** Bills a retired id at its successor's weight: the successor is the model that actually ran. */
export const getTokenCostByModel = (model: SupportedModel) => aiModelTokenCost[normalizeModelId(model)]

export const getProviderSpec = (provider: AIProvider, type: 'client' | 'server' = 'server') => {
  const spec = providerSpecs[provider]
  if (type === 'client') {
    return {
      ...spec,
      baseURL: `https://tunasong.com/stream/proxy`,
    }
  }
  return spec
}

export const getProviderApiKeySecret = (provider: AIProvider) => providerSpecs[provider]?.apiKeySecret

const createProxyFetch = ({ model, authToken }: { model: SupportedModel; authToken: string }) => {
  /** Proxy fetch to our API   */
  const proxyFetch: typeof fetch = async (input: string | Request | URL, options: RequestInit = {}) => {
    // Send the body, headers in the body. We do this to preserve the headers
    // and avoid CORS issues
    const url = typeof input === 'string' ? input : input.toString()

    const { headers, body } = input instanceof Request ? input : options

    // Remove the authorization header from the request

    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        model,
        provider: getProviderByModel(model),
        payload: body,
        headers,
      }),
    })
  }
  return proxyFetch
}

/** Custom provider. authToken is JWT token in client mode, and API key in server mode */
const getAIModelInternal = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedModel
}) => {
  const proxyFetch = createProxyFetch({ model, authToken })
  const baseURL = type === 'client' ? 'https://tunasong.com/stream/proxy' : undefined
  const apiKey = type === 'server' ? authToken : 'NOT_USED'

  /** Create custom providers with proxyFetch */
  const openai = createOpenAI({
    baseURL,
    apiKey,
    fetch: type === 'client' ? proxyFetch : fetch,
  })
  const anthropic = createAnthropic({
    baseURL,
    fetch: type === 'client' ? proxyFetch : fetch,
    apiKey,
  })
  const gemini = createGoogleGenerativeAI({
    baseURL,
    fetch: type === 'client' ? proxyFetch : fetch,
    apiKey,
  })

  const transcribeModels = {
    'whisper-1': openai.transcription('whisper-1'),
    'gpt-4o-transcribe': openai.transcription('gpt-4o-transcribe'),
    'gpt-4o-mini-transcribe': openai.transcription('gpt-4o-mini-transcribe'),
  }

  /**
   * ADD YOUR MODELS HERE
   */
  const languageModels = {
    // OpenAI models
    // https://developers.openai.com/api/docs/models
    'gpt-6.1-sol': openai('gpt-6.1-sol'),
    'gpt-6-luna': openai('gpt-6-luna'),
    'gpt-4o-audio-preview': openai('gpt-4o-audio-preview'),
    // Anthropic models
    // https://platform.claude.com/docs/en/about-claude/models/overview
    'claude-opus-5-5': anthropic('claude-opus-5-5'),
    'claude-sonnet-5-5': anthropic('claude-sonnet-5-5'),
    'claude-haiku-4-5': anthropic('claude-haiku-4-5'),
    // Google Gemini models
    // https://ai.google.dev/gemini-api/docs/models
    'gemini-3.8-flash': gemini('gemini-3.8-flash'),
    'gemini-2.5-pro': gemini('gemini-2.5-pro'),
  } satisfies Record<Exclude<SupportedModel, NonLanguageModel>, unknown>

  const provider = customProvider({
    languageModels,

    fallbackProvider: openai,
  })
  return { provider, openai, anthropic, gemini, languageModels, transcribeModels }
}

export const getAIModel = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedModel
}) => {
  // An unknown id would fall through `customProvider` to the OpenAI fallback, so a
  // retired id (e.g. a stored Anthropic one) must be resolved to its successor first.
  const resolved = normalizeModelId(model)
  const { provider } = getAIModelInternal({ type, authToken, model: resolved })
  return provider.languageModel(resolved)
}

export const getTranscriptionModel = ({
  type,
  authToken,
  model,
}: {
  type: 'client' | 'server'
  authToken: string
  model: SupportedTranscribeModel
}) => {
  const { openai } = getAIModelInternal({ type, authToken, model })

  const transcriptionModel = openai.transcription(model)
  return transcriptionModel
}

// The cost is per minute of transcription
export const transcribeModelCost = {
  'whisper-1': 100,
  // @todo gpt-4o requires mp3 or wav
  // 'gpt-4o-transcribe': 100,
}

export type SupportedTranscribeModel = keyof typeof transcribeModelCost

interface TokenPrice {
  inputPerMTok: number
  outputPerMTok: number
}

/**
 * Published list prices, USD per million tokens (standard tier, global
 * routing, short-context rate, no batch or cache discount). Recorded so the
 * weights in `aiModelTokenCost` can be re-derived, and checked by
 * `providers.test.ts`, from the providers' own pricing pages rather than from
 * memory. Prices as of 2026-10-07:
 *
 * - OpenAI:    https://developers.openai.com/api/docs/pricing
 *              (gpt-6.1-sol: https://developers.openai.com/api/docs/models/gpt-6.1-sol,
 *               gpt-6-luna:  https://developers.openai.com/api/docs/models/gpt-6-luna)
 * - Anthropic: https://platform.claude.com/docs/en/about-claude/pricing
 * - Google:    https://ai.google.dev/gemini-api/docs/pricing
 *
 * `gemini-3.8-flash` records its standard rate from 2027-01-01 ($1.50 / $7.50).
 * Google lists an introductory $0.75 / $3.75 through 2026-12-31; using the
 * standard rate over-charges quota for under three months instead of
 * under-charging from January onwards.
 *
 * `gpt-4o-audio-preview` is deliberately absent: OpenAI does not list it on its
 * pricing page, so its weight is carried, not derived.
 */
export const modelTokenPrices = {
  'gpt-6.1-sol': { inputPerMTok: 2, outputPerMTok: 10 },
  'gpt-6-luna': { inputPerMTok: 0.1, outputPerMTok: 0.5 },
  'claude-opus-5-5': { inputPerMTok: 4, outputPerMTok: 20 },
  'claude-sonnet-5-5': { inputPerMTok: 2, outputPerMTok: 10 },
  'claude-haiku-4-5': { inputPerMTok: 1, outputPerMTok: 5 },
  'gemini-3.8-flash': { inputPerMTok: 1.5, outputPerMTok: 7.5 },
  'gemini-2.5-pro': { inputPerMTok: 1.25, outputPerMTok: 10 },
} as const satisfies Record<string, TokenPrice>

export type PricedModel = keyof typeof modelTokenPrices

/**
 * Share of a billed token assumed to be input. `updateTokenQuota` only sees
 * `totalTokens`, so a weight has to assume a mix. Agent turns dominate usage
 * and are input-heavy: every step resends the preamble, entity context and tool
 * schemas (several thousand tokens) and gets back a few hundred tokens of text
 * or a tool call. 90/10 matches that shape. Plain chat sits closer to 80/20,
 * which would move no weight by more than about 10%.
 */
export const quotaInputShare = 0.9

/**
 * The anchor: the current Sonnet tier weighs 2, the weight Sonnet has always
 * had. Re-anchored from `claude-sonnet-4-6` to its successor `claude-sonnet-5-5`
 * when the model ids moved to the Claude 5 / GPT-6 generation.
 */
export const quotaAnchorModel: PricedModel = 'claude-sonnet-5-5'
export const quotaAnchorWeight = 2

const blendedPricePerMTok = ({ inputPerMTok, outputPerMTok }: TokenPrice) =>
  quotaInputShare * inputPerMTok + (1 - quotaInputShare) * outputPerMTok

/**
 * The quota weight formula, rounded to two decimals:
 *
 *   blended(m) = 0.9 × inputPrice(m) + 0.1 × outputPrice(m)
 *   weight(m)  = anchorWeight × blended(m) / blended(claude-sonnet-5-5)
 *
 * One weighted token is then worth the same number of dollars whichever model
 * spent it ($1.40 per million weighted tokens at the sonnet anchor).
 */
export const quotaWeightFromPrice = (price: TokenPrice) =>
  Math.round(
    ((quotaAnchorWeight * blendedPricePerMTok(price)) / blendedPricePerMTok(modelTokenPrices[quotaAnchorModel])) * 100
  ) / 100

/**
 * This also defines the models.
 *
 * These are *relative* quota weights consumed by `updateTokenQuota`
 * (`usage.aiTokens += totalTokens × weight`), not prices. Each token-billed
 * model with a published price has the weight
 * `quotaWeightFromPrice(modelTokenPrices[model])`. The literals stay so the
 * table reads at a glance, and `providers.test.ts` fails if one drifts from its
 * recorded price.
 *
 * Exceptions, which are not token-billed or have no published price:
 * - `whisper-1`: transcription is billed per minute (`transcribeModelCost`).
 * - `gpt-4o-audio-preview`: no published price, so the slot's existing weight
 *   is carried rather than invented.
 * - `elevenlabs-music-*`: billed per second of audio (see music-task.ts).
 *
 * Retired ids are not in this table; see `legacyModelAliases`. Image
 * generation does not use it either; see `imageGenerationQuotaWeight`.
 */
export const aiModelTokenCost = {
  'whisper-1': 0.006,
  'gpt-6.1-sol': 2,
  'gpt-6-luna': 0.1,
  'gpt-4o-audio-preview': 5,
  'claude-opus-5-5': 4,
  'claude-sonnet-5-5': 2,
  'claude-haiku-4-5': 1,
  'gemini-3.8-flash': 1.5,
  'gemini-2.5-pro': 1.52,
  // v2 is the current default (introducing-music-v2).
  'elevenlabs-music-v1': 100,
  'elevenlabs-music-v2': 100,
}

/** Registry entries that are not chat/language models. */
type NonLanguageModel = 'whisper-1' | 'elevenlabs-music-v1' | 'elevenlabs-music-v2'

/**
 * Retired model ids and the current id each one resolves to.
 *
 * Profiles, band-space configs and entity agents persist a model id, and
 * stored data outlives a registry bump. Rather than reject those ids (a zod
 * enum would 400 a request, and `aiContextSettingsSchema` would silently drop
 * the user's choice), they stay accepted and resolve to their successor:
 * `aiModelsSchema` normalises on parse, and `getAIModel`, `getProviderByModel`
 * and `getTokenCostByModel` normalise too, because stored agent configs reach
 * them through an `as SupportedModel` cast rather than through the schema.
 *
 * `gpt-6-astra` was never registered. It maps to Sol, the OpenAI tier we
 * offer, so that a config naming the top OpenAI tier lands on the supported one.
 */
export const legacyModelAliases = {
  'gpt-6-astra': 'gpt-6.1-sol',
  'gpt-5.5': 'gpt-6.1-sol',
  'gpt-5.4': 'gpt-6.1-sol',
  'gpt-5.4-mini': 'gpt-6-luna',
  'claude-opus-4-7': 'claude-opus-5-5',
  'claude-opus-4-6': 'claude-opus-5-5',
  'claude-sonnet-4-6': 'claude-sonnet-5-5',
  'claude-sonnet-4-5': 'claude-sonnet-5-5',
  'gemini-2.5-flash': 'gemini-3.8-flash',
} as const satisfies Record<string, SupportedModel>

export type LegacyModelId = keyof typeof legacyModelAliases

const legacyModelIds = Object.keys(legacyModelAliases) as LegacyModelId[]

export function isLegacyModelId(model: string): model is LegacyModelId {
  return Object.hasOwn(legacyModelAliases, model)
}

/** Resolve a retired model id to its successor; any other id passes through unchanged. */
export const normalizeModelId = <T extends string>(model: T | LegacyModelId): T | SupportedModel =>
  isLegacyModelId(model) ? legacyModelAliases[model] : model

/**
 * Quota weight per token of an image-generation Responses call. It is frozen at
 * the weight images were billed at before the token weights were re-derived
 * from prices (the old `gpt-5.4` weight of 12, GH #1958), so that the
 * re-derivation does not make images cheaper as a side effect.
 *
 * Image cost is driven by the `image_generation` tool, not by the chat model
 * that orchestrates the call. OpenAI's current image models
 * (gpt-image-2.5-sunburst / -flare) list $5 per MTok for text input and $30 for
 * image output. Under the sonnet-5-5 anchor, an output-dominated image call
 * would derive to roughly 15 to 20, so 12 is a floor carried from before, not a
 * formula result: the Responses `usage.total_tokens` mixes orchestrator and
 * image tokens in proportions we do not see.
 */
export const imageGenerationQuotaWeight = 12

export type SupportedModel = keyof typeof aiModelTokenCost

/**
 * ElevenLabs music-generation model ids (as sent to the ElevenLabs API), kept
 * here in the browser-safe registry so both the Lambda worker and the client
 * RTK query can share the union without pulling in the node-only SDK wrapper.
 * v2 adds better vocals/arrangement, multilingual, and section-by-section
 * composition — see https://elevenlabs.io/blog/introducing-music-v2.
 */
export const elevenLabsMusicModels = ['music_v1', 'music_v2'] as const
export type ElevenLabsMusicModel = (typeof elevenLabsMusicModels)[number]

export const defaultElevenLabsMusicModel: ElevenLabsMusicModel = 'music_v2'

/** Map an ElevenLabs music model id to its internal quota model key. */
export const musicQuotaModel = (modelId: ElevenLabsMusicModel): SupportedModel =>
  modelId === 'music_v2' ? 'elevenlabs-music-v2' : 'elevenlabs-music-v1'

const supportedModelsByProvider: Record<string, [SupportedModel, ...SupportedModel[]]> = {
  openai: [
    'gpt-6.1-sol',
    'gpt-6-luna',
    'gpt-4o-audio-preview',
    // 'whisper-1',
  ],
  anthropic: ['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-4-5'],
  gemini: ['gemini-3.8-flash', 'gemini-2.5-pro'],
}

const aiModelNames: SupportedModel[] = Object.keys(aiModelTokenCost) as SupportedModel[]

const nonLanguageModels: readonly string[] = [
  'whisper-1',
  'elevenlabs-music-v1',
  'elevenlabs-music-v2',
] satisfies NonLanguageModel[]

/** Chat/language models selectable in the UI (excludes transcription, music-only and retired models) */
export const selectableModelIds = aiModelNames.filter(m => !nonLanguageModels.includes(m)) as [
  SupportedModel,
  ...SupportedModel[],
]

/**
 * A chat/language model id. Accepts a retired id from `legacyModelAliases` and
 * resolves it to its successor, so a profile, space or agent saved before a
 * registry bump still parses and runs. Use `selectableModelIds` to list the
 * choices offered in a UI.
 */
export const aiModelsSchema = z.enum([...selectableModelIds, ...legacyModelIds]).transform(normalizeModelId)
